import {
  Building2,
  FileText,
  Lock,
  Package,
  QrCodeIcon,
  Scale,
  Tag,
  Triangle
} from 'lucide-react'
import QRCode from 'qrcode'
import React, { useEffect, useState } from 'react'

import { useMasterBillActions } from '../stores/useMasterBillStore'

import ConfirmDialog from '@/components/common/ConfirmDialog'
import PrintDialog from '@/components/common/PrintDialog'
import ToggleSwitch from '@/components/form/ToggleSwitch'
import { customsV2Service, outboundV2Service } from '@/services'
import { HouseBillSchema } from '@/services/CustomsV2Service'
import { useUIStore } from '@/stores/useUIStore'
import { HouseBillStatus } from '@/types/customs'
import { HttpError } from '@/types/errors'

interface HouseBillItemProps {
  houseBill: HouseBillSchema
  index: number
}

const HouseBillItem: React.FC<HouseBillItemProps> = ({ houseBill }) => {
  const { showError, showSuccess } = useUIStore()
  // 获取主单相关的操作

  const {
    setHouseBill,
    showNoteDialog,
    fetchMasterBill,
    updateFindHouseInMaster
  } = useMasterBillActions()
  // 本地状态管理权限状态
  const [naccsPermitStatus, setNaccsPermitStatus] = useState(
    houseBill.naccsPermitStatus
  )

  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [isUpdatingNaccs, setIsUpdatingNaccs] = useState(false)

  // 更新状态为查验中
  const updateStatusToInspect = async (houseBillId: number) => {
    const { success, message } = await customsV2Service.inspect(houseBillId)
    if (success) {
      showSuccess('状态更新成功')
      fetchMasterBill(houseBill.masterBillId)
    } else {
      showError(message)
    }
  }

  // 取消查验逻辑
  const cancelInspect = async (houseBillId: number) => {
    const { success, message } =
      await customsV2Service.inspectCancel(houseBillId)
    if (success) {
      showSuccess('取消查验成功')
      fetchMasterBill(houseBill.masterBillId)
    } else {
      showError(message)
    }
  }

  // 当 houseBill 更新时同步本地状态
  useEffect(() => {
    setNaccsPermitStatus(houseBill.naccsPermitStatus)
    // setImportPermitStatus(houseBill.importPermitStatus)
  }, [houseBill.naccsPermitStatus, houseBill.importPermitStatus])

  // nacss 更新
  const handleNacssChange = async (value: boolean) => {
    if (isUpdatingNaccs) return // 防止重复点击

    setIsUpdatingNaccs(true)
    const previousStatus = naccsPermitStatus
    setNaccsPermitStatus(value) // 乐观更新，立即反馈UI

    try {
      if (value) {
        const { success, message, data } = await outboundV2Service.nacssPassed(
          houseBill.id
        )
        if (success && data) {
          showSuccess(message)
          updateFindHouseInMaster(data)
        } else {
          setNaccsPermitStatus(previousStatus) // 失败回滚
          showError(message)
        }
      } else {
        const { success, message, data } = await outboundV2Service.nacssReject(
          houseBill.id
        )
        if (success && data) {
          showSuccess(message)
          updateFindHouseInMaster(data)
        } else {
          setNaccsPermitStatus(previousStatus) // 失败回滚
          showError(message)
        }
      }
    } catch {
      setNaccsPermitStatus(previousStatus) // 异常回滚
      showError('操作失败，请重试')
    } finally {
      setIsUpdatingNaccs(false)
    }
  }

  // 打开备注编辑对话框
  const openNoteDialog = (houseBillId: number) => {
    setHouseBill(houseBillId)
    showNoteDialog()
  }

  // 打印二维码
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('')
  const [qrCodeVisible, setQrCodeVisible] = useState<boolean>(false)
  const [qrCodeOldVisible, setQrCodeOldVisible] = useState<boolean>(false)

  const qrPrintStyles = `
    @page {
      size: 60mm 32mm;
      margin: 0;
    }
    html,
    body {
      margin: 0;
      padding: 0;
      width: 60mm;
      height: 32mm;
    }
    body {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #ffffff;
    }
    body > div {
      margin-left: 40mm;
      margin-top: 2mm;
    }
  `

  const generateQrCode = async () => {
    const url = await QRCode.toDataURL(houseBill.houseBlNumber, {
      width: 90,
      margin: 2,
      errorCorrectionLevel: 'H'
    })
    setQrCodeUrl(url)
    return url
  }

  const handlePrintQRCode = async () => {
    try {
      const url = await generateQrCode()
      if (url) setQrCodeVisible(true)
    } catch {
      showError('生成二维码失败，请重试')
    }
  }

  const handlePrintQRCodeOld = async () => {
    try {
      const url = await generateQrCode()
      if (url) setQrCodeOldVisible(true)
    } catch {
      showError('生成二维码失败，请重试')
    }
  }

  // 处理归档确认
  const handleArchiveConfirm = async () => {
    if (houseBill.status !== HouseBillStatus.PENDING_CONFIRMATION) {
      setShowArchiveConfirm(false)
      return
    }
    try {
      const { success, message } = await customsV2Service.archive(houseBill.id)
      if (success) {
        showSuccess(message)
        fetchMasterBill(houseBill.masterBillId)
      } else {
        showError(message)
      }
    } finally {
      setShowArchiveConfirm(false)
    }
  }

  return (
    <div className={`p-4 lg:p-6 bg-white shadow-sm rounded-lg`}>
      <div className="space-y-2">
        <div className="p-2">
          <div className="text-lg"></div>
          <div className="flex justify-between mb-4">
            <div>
              {houseBill.status === 'PENDING_INBOUND' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0">
                  {houseBill.statusDisplay}
                </span>
              )}
              {houseBill.status === 'PENDING_INSPECTION' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0">
                  {houseBill.statusDisplay}
                </span>
              )}
              {houseBill.status === 'UNDER_REVIEW' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0">
                  {houseBill.statusDisplay}
                </span>
              )}
              {houseBill.status === 'UNDER_INSPECTION' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex-shrink-0">
                  {houseBill.statusDisplay}
                </span>
              )}
              {[
                'PENDING_OUTBOUND',
                'OUTBOUND_IN_PROGRESS',
                'PENDING_DELIVERY'
              ].includes(houseBill.status) && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 flex-shrink-0">
                  {houseBill.statusDisplay}
                </span>
              )}

              {['PENDING_CONFIRMATION'].includes(houseBill.status) && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex-shrink-0">
                  {houseBill.statusDisplay}
                </span>
              )}

              {['COMPLETED'].includes(houseBill.status) && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-400 flex-shrink-0">
                  {houseBill.statusDisplay}
                </span>
              )}

              <span className="pl-2"> HBL: {houseBill.houseBlNumber}</span>
            </div>
            <div className="flex space-x-2">
              {houseBill.status === 'UNDER_REVIEW' && (
                <button
                  onClick={() => updateStatusToInspect(houseBill.id)}
                  className="px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 transition-colors"
                >
                  查验
                </button>
              )}

              {houseBill.isInspectionRequired === true && (
                <button
                  onClick={() => cancelInspect(houseBill.id)}
                  className="px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 transition-colors"
                >
                  取消查验
                </button>
              )}

              {[
                HouseBillStatus.PENDING_OUTBOUND,
                HouseBillStatus.OUTBOUND_IN_PROGRESS,
                HouseBillStatus.PENDING_DELIVERY,
                HouseBillStatus.PENDING_INSPECTION
              ].includes(houseBill.status as HouseBillStatus) && (
                <>
                  <button
                    onClick={handlePrintQRCode}
                    className="px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 transition-colors"
                    title="打印二维码（新版）"
                  >
                    <QrCodeIcon />
                  </button>
                  <button
                    onClick={handlePrintQRCodeOld}
                    className="px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-colors"
                    title="打印二维码（旧版）"
                  >
                    <QrCodeIcon />
                  </button>
                </>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation()
                  openNoteDialog(houseBill.id)
                }}
                className="px-2 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-600 hover:bg-yellow-200 hover:text-yellow-800 transition-colors"
                title={
                  houseBill.note ? `更新备注: ${houseBill.note}` : '添加备注'
                }
              >
                <span>备注</span>
              </button>
            </div>
          </div>
          <div className="divide-y divide-dashed divide-gray-300">
            <div className="flex justify-between items-center mt-2">
              <span className="text-gray-500 flex items-center gap-1">
                <Tag className="w-4 h-4" />
                唛头数
              </span>
              <span className="text-black">
                {houseBill.cargoDetails?.length || '-'} 个
              </span>
            </div>
            <div className="flex justify-between items-center mt-2 pt-2">
              <span className="text-gray-500 flex items-center gap-1">
                <Package className="w-4 h-4" />
                箱货数
              </span>
              <span className="text-black">{houseBill.packages || '-'}</span>
            </div>
            <div className="flex justify-between items-center mt-2 pt-2">
              <span className="text-gray-500 flex items-center gap-1">
                <Scale className="w-4 h-4" />
                重量
              </span>
              <span className="text-black">{houseBill.weightKg || '-'} KG</span>
            </div>
            <div className="flex justify-between items-center mt-2 pt-2">
              <span className="text-gray-500 flex items-center gap-1">
                <Triangle className="w-4 h-4" />
                体积
              </span>
              <span className="text-black">
                {houseBill.volumeCbm || '-'} CBM
              </span>
            </div>
            {(houseBill.status === 'UNDER_REVIEW' ||
              houseBill.status === 'PENDING_OUTBOUND' ||
              houseBill.status === 'COMPLETED' ||
              houseBill.status === 'PENDING_CONFIRMATION') && (
              <>
                <div className="flex justify-between items-center mt-2 pt-2">
                  <span className="text-gray-500 flex items-center gap-1">
                    <Building2 className="w-4 h-4" />
                    NACCS許可
                  </span>
                  <ToggleSwitch
                    disabled={
                      isUpdatingNaccs ||
                      (houseBill.status !== 'UNDER_REVIEW' &&
                        houseBill.status !== 'PENDING_OUTBOUND')
                    }
                    color="green"
                    label=""
                    value={naccsPermitStatus}
                    onChange={(value) => handleNacssChange(value)}
                  />
                </div>

                <div className="flex justify-between items-center mt-2 pt-2">
                  <span className="text-gray-500 flex items-center gap-1">
                    <Lock className="w-4 h-4" />
                    輸入許可
                  </span>
                </div>

                {houseBill.status === 'PENDING_CONFIRMATION' && (
                  <div className="flex justify-between items-center mt-2 pt-2">
                    <button
                      onClick={() => setShowArchiveConfirm(true)}
                      className={`w-full px-3 py-1 rounded-md font-medium transition-colors ${
                        houseBill.status !== 'PENDING_CONFIRMATION'
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      }`}
                    >
                      归档
                    </button>
                  </div>
                )}
              </>
            )}
            {houseBill.note !== '' && (
              <div className="flex justify-between items-center mt-2 pt-2 min-h-[2rem]">
                <span className="text-gray-500 flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  备注
                </span>
                <span className="text-black">{houseBill.note}</span>
              </div>
            )}
          </div>
        </div>

        <div className="divide-y divide-dashed divide-gray-200 text-sm">
          {houseBill &&
            houseBill.cargoDetails?.map((cargo) => (
              <div
                key={cargo.id}
                className="flex justify-between items-center mt-2 pt-2"
              >
                <span className="text-gray-600 flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  {cargo.cargoNumber}
                </span>
                <span className="flex items-center gap-1">
                  {cargo.packages}
                  <Package className="w-3 h-3" />
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* 归档确认对话框 */}
      <ConfirmDialog
        title={'确认归档'}
        onCancel={() => setShowArchiveConfirm(false)}
        onConfirm={() => handleArchiveConfirm()}
        isOpen={showArchiveConfirm}
      >
        <p className="text-gray-600 mb-6">
          确定要将 HBL: {houseBill.houseBlNumber} 进行归档操作吗？
          <br />
          <span className="text-sm text-gray-500">
            此操作将更新输入许可状态
          </span>
        </p>
      </ConfirmDialog>

      {/* 打印组件 */}
      <PrintDialog
        pageWidth={60}
        pageHeight={30}
        visible={qrCodeVisible}
        isMultiPage={false}
        pageCssContent={qrPrintStyles}
        onCancel={() => setQrCodeVisible(false)}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            height: '100%',
            width: '100%',
            gap: '4px'
          }}
        >
          <img src={qrCodeUrl} alt="QR Code" />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              height: '90px',
              paddingTop: '10px',
              paddingRight: '10px',
              gap: '2px'
            }}
          >
            <span style={{ fontSize: '10px', lineHeight: '1', width: '100%', textAlign: 'right' }}>
              {new Date().toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              })}
            </span>
            <hr style={{ width: '100%', border: 'none', borderTop: '1px dashed #000', margin: '2px 0' }} />
            <span
              style={{ fontSize: '9px', lineHeight: '1.5', wordBreak: 'break-all' }}
            >
              {houseBill.houseBlNumber}
            </span>
          </div>
        </div>
      </PrintDialog>

      {/* 旧版打印组件 */}
      <PrintDialog
        pageWidth={60}
        pageHeight={30}
        visible={qrCodeOldVisible}
        isMultiPage={false}
        pageCssContent={qrPrintStyles}
        onCancel={() => setQrCodeOldVisible(false)}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            height: '100%',
            width: '100%'
          }}
        >
          <img src={qrCodeUrl} alt="QR Code" />
          <span style={{ fontSize: '9px' }}>
            {houseBill.houseBlNumber}
          </span>
        </div>
      </PrintDialog>
    </div>
  )
}

export default HouseBillItem
